import { Fragment, useEffect, useState } from "react";
import { appConfirm } from "../../components/ConfirmDialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, ChevronDown, ChevronRight, Plus, Archive, Trash2, ArchiveRestore, Search } from "lucide-react";
import { PRICE_EFFECT, UNIT_EFFECT } from "../../lib/pricingEffects";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const gbp = (n) => (n === null || n === undefined || n === "") ? "—" : `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;
const UNIT_LABEL = { fixed: "Fixed", each: "Each", sqm: "Per m²", lm: "Per lm" };

function deviates(price, ref) {
  if (!ref || Number(ref) <= 0 || price === null || price === undefined || price === "") return false;
  return Math.abs(Number(price) - Number(ref)) / Number(ref) > 0.20;
}
const FIELDS = ["label", "site_label", "unit", "price", "show_to_client", "category", "wickes_url", "lords_price"];
const snapshot = (items) => {
  const o = {};
  items.forEach((i) => { o[i.key] = {}; FIELDS.forEach((f) => { o[i.key][f] = i[f]; }); });
  return o;
};
const Dot = () => <span title="Unsaved change" className="w-1.5 h-1.5 rounded-full bg-[#D97706] inline-block ml-1" data-testid="unsaved-dot" />;

export function PricingPanel({ onPricesSaved }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [orig, setOrig] = useState({});
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/pricing");
      setData(r.data); setRows(r.data.items); setOrig(snapshot(r.data.items));
    } catch { toast.error("Could not load pricing"); }
  };
  useEffect(() => { load(); }, []);

  const setField = (key, field, val) => setRows((prev) => prev.map((i) => (i.key === key ? { ...i, [field]: val } : i)));
  const norm = (v) => (v === undefined || v === null ? "" : v);
  const dirtyFields = (it) => { const o = orig[it.key] || {}; return FIELDS.filter((f) => String(norm(it[f])) !== String(norm(o[f]))); };
  const dirtyRows = (rows || []).filter((it) => dirtyFields(it).length > 0);
  const hasUnsaved = dirtyRows.length > 0;

  const saveAll = async () => {
    setBusy(true);
    try {
      const items = dirtyRows.map((it) => {
        const payload = { key: it.key };
        dirtyFields(it).forEach((f) => { payload[f] = it[f] === "" && f === "price" ? 0 : it[f]; });
        return payload;
      });
      const r = await api.put("/admin/pricing", { items });
      setData(r.data); setRows(r.data.items); setOrig(snapshot(r.data.items));
      toast.success("Prices saved — live in the quote engine");
      if (onPricesSaved) onPricesSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save prices"); }
    setBusy(false);
  };

  const refresh = async (which) => {
    setRefreshing(which);
    try {
      const r = await api.post(which === "wickes" ? "/admin/pricing/refresh-wickes" : "/admin/pricing/refresh-market");
      await load();
      toast.success(which === "wickes" ? `Wickes: ${r.data.fetched} updated, ${r.data.failed} blocked/stale` : `Market benchmarks refreshed (${r.data.updated})`);
    } catch { toast.error("Refresh failed"); }
    setRefreshing("");
  };

  const archive = async (it) => {
    try { const r = await api.post(`/admin/pricing/item/${it.key}/archive`, { archived: !it.archived }); setData(r.data); setRows(r.data.items); setOrig(snapshot(r.data.items)); toast.success(it.archived ? "Restored" : "Archived"); if (onPricesSaved) onPricesSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const del = async (it) => {
    if (!(await appConfirm({ title: `Delete "${it.label}"?`, description: "This cannot be undone.", confirmLabel: "Delete", destructive: true }))) return;
    try { const r = await api.delete(`/admin/pricing/item/${it.key}`); setData(r.data); setRows(r.data.items); setOrig(snapshot(r.data.items)); toast.success("Deleted"); if (onPricesSaved) onPricesSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const toggleExpand = (key) => setExpanded((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  if (!data) return <div className="label-eyebrow" data-testid="pricing-loading">Loading pricing…</div>;

  const q = search.trim().toLowerCase();
  const visibleRows = rows.filter((it) => (showArchived || !it.archived) && (!q || it.key.toLowerCase().includes(q) || (it.label || "").toLowerCase().includes(q)));
  const clientItems = rows.filter((it) => it.show_to_client && !it.archived);

  const SaveBar = ({ pos }) => (
    <div className="flex items-center gap-3 flex-wrap">
      <button data-testid={`pricing-save-all-${pos}`} onClick={saveAll} disabled={busy || !hasUnsaved} className="de-btn-primary disabled:opacity-50">{busy ? "Saving…" : "Save all prices"}</button>
      {hasUnsaved
        ? <span data-testid={`pricing-unsaved-${pos}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#B45309]"><span className="w-2 h-2 rounded-full bg-[#D97706] inline-block" />{dirtyRows.length} row{dirtyRows.length > 1 ? "s" : ""} with unsaved changes</span>
        : <span className="text-xs text-[#6B7280]">All changes saved</span>}
    </div>
  );

  const Toggle = ({ on, onClick, testid }) => (
    <button data-testid={testid} onClick={onClick} role="switch" aria-checked={on}
      className={`relative w-9 h-5 rounded-full transition-colors ${on ? "bg-[#C79A3B]" : "bg-[#D1D5DB]"}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${on ? "translate-x-4" : ""}`} />
    </button>
  );

  return (
    <div data-testid="pricing-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="label-eyebrow">Quote pricing</div>
          <h2 className="text-xl font-black tracking-tight">Pricing — what customers are charged</h2>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">Every price the instant quote uses, in plain English. Edit the <strong>name</strong> or the <strong>£</strong> and press Save — changes go live in the quote engine straight away. The grey line under each item tells you exactly what changing it does.</p>
          <p className="text-xs text-[#6B7280] mt-2 max-w-2xl"><strong className="text-[#12263A]">Show price</strong> displays that price as a "+£" hint next to the option on the <strong className="text-[#12263A]">configurator page only</strong> — it does not appear anywhere else on the website. Benchmarks (inside a row's <ChevronRight size={11} className="inline" /> drawer) are reference only and never affect a quote.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="pricing-add-item" onClick={() => setAdding(true)} className="de-btn-primary text-xs inline-flex items-center gap-1.5"><Plus size={14} />Add item</button>
          <button data-testid="pricing-refresh-market" onClick={() => refresh("market")} disabled={!!refreshing} className="de-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"><RefreshCw size={13} className={refreshing === "market" ? "animate-spin" : ""} />Refresh</button>
          <button data-testid="pricing-refresh-wickes" onClick={() => refresh("wickes")} disabled={!!refreshing} className="de-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"><RefreshCw size={13} className={refreshing === "wickes" ? "animate-spin" : ""} />Refresh Wickes</button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input data-testid="pricing-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by item key or label…" className="w-full border border-[#E5E7EB] pl-9 pr-3 py-2 text-sm" />
        </div>
        <label className="text-xs text-[#6B7280] inline-flex items-center gap-1.5"><input type="checkbox" data-testid="pricing-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />Show archived</label>
        <SaveBar pos="top" />
      </div>

      {adding && <AddItemForm categories={data.categories} units={data.units} onClose={() => setAdding(false)} onCreated={(r) => { setAdding(false); setData(r); setRows(r.items); setOrig(snapshot(r.items)); if (onPricesSaved) onPricesSaved(); }} />}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 overflow-x-auto border border-[#E5E7EB]">
          <table className="w-full text-sm" data-testid="pricing-table">
            <thead>
              <tr className="bg-[#12263A] text-white text-left text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2">Item / label</th>
                <th className="px-2 py-2">Unit</th>
                <th className="px-2 py-2 text-right">Our price</th>
                <th className="px-2 py-2 text-center" title="Display this price on the configurator page only">Show price</th>
                <th className="px-2 py-2 text-center">More</th>
              </tr>
            </thead>
            <tbody>
              {data.categories.map((cat) => {
                const catRows = visibleRows.filter((r) => r.category === cat);
                if (catRows.length === 0) return null;
                return (
                  <CategoryGroup key={cat} cat={cat} rows={catRows} expanded={expanded} toggleExpand={toggleExpand}
                    setField={setField} dirtyFields={dirtyFields} archive={archive} del={del} Toggle={Toggle} />
                );
              })}
              {visibleRows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#6B7280]">No items match "{search}".</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="xl:col-span-1">
          <div className="sticky top-4 border border-[#E5E7EB] bg-[#FBFAF8] p-4" data-testid="pricing-preview">
            <div className="label-eyebrow mb-1">Prices shown in the configurator</div>
            <p className="text-xs text-[#6B7280] mb-3">These appear as "+£" hints next to the matching option on the configurator page only. The instant quote itself stays a single combined figure.</p>
            {clientItems.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">No prices shown yet. Toggle <b>Show price</b> on a row to display it in the configurator.</p>
            ) : (
              <ul className="space-y-1.5">
                {clientItems.map((it) => (
                  <li key={it.key} data-testid={`pricing-preview-item-${it.key}`} className="flex justify-between gap-2 text-sm border-b border-[#EEE9E0] pb-1.5">
                    <span className="text-[#12263A]">{it.label}{it.unit !== "fixed" && <span className="text-[10px] text-[#9CA3AF]"> · {UNIT_LABEL[it.unit]}</span>}</span>
                    <span className="font-bold tabular-nums">{gbp(it.price === "" ? 0 : it.price)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6"><SaveBar pos="bottom" /></div>
    </div>
  );
}

function CategoryGroup({ cat, rows, expanded, toggleExpand, setField, dirtyFields, archive, del, Toggle }) {
  return (
    <>
      <tr className="bg-[#F4F4F4] border-t border-[#E5E7EB]"><td colSpan={5} className="px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-[#6B7280]">{cat}</td></tr>
      {rows.map((it) => {
        const df = dirtyFields(it);
        const ref = it.is_material ? (it.lords_price ?? it.wickes_price ?? it.market_benchmark) : it.market_benchmark;
        const amber = deviates(it.price, ref);
        const open = expanded.has(it.key);
        const isDirty = (f) => df.includes(f);
        return (
          <Fragment key={it.key}>
            <tr data-testid={`pricing-row-${it.key}`} className={`border-t border-[#E5E7EB] ${it.show_to_client ? "border-l-4 border-l-[#C79A3B] bg-[#FFFDF8]" : ""} ${it.archived ? "opacity-50" : ""}`}>
              <td className="px-3 py-2 align-top">
                <input data-testid={`pricing-label-${it.key}`} value={it.label || ""} onChange={(e) => setField(it.key, "label", e.target.value)}
                  className="w-full bg-transparent border-b border-transparent hover:border-[#E5E7EB] focus:border-[#12263A] outline-none text-sm font-medium" />
                <div className="text-[10px] text-[#6B7280] mt-0.5 leading-snug" data-testid={`pricing-effect-${it.key}`}>{PRICE_EFFECT[it.key] || UNIT_EFFECT[it.unit] || ""}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="font-mono text-[10px] text-[#9CA3AF]">{it.key}</span>
                  {it.builtin ? <span className="text-[9px] uppercase tracking-wide text-[#9CA3AF] bg-[#F0EFEC] px-1">engine</span> : <span className="text-[9px] uppercase tracking-wide text-[#1F6B3B] bg-[#E7F4EC] px-1">add-on</span>}
                  {it.archived && <span className="text-[9px] uppercase tracking-wide text-[#6B7280] bg-[#F0EFEC] px-1">archived</span>}
                  {isDirty("label") && <Dot />}
                </div>
                {it.site_label_key && (
                  <div className="mt-1.5 border-t border-dashed border-[#EDEBE6] pt-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-[#C79A3B] font-bold flex items-center gap-1">Label shown on the site {isDirty("site_label") && <Dot />}</div>
                    <input data-testid={`pricing-sitelabel-${it.key}`} value={it.site_label || ""} onChange={(e) => setField(it.key, "site_label", e.target.value)}
                      className="w-full bg-[#FFFDF8] border-b border-[#E5E7EB] focus:border-[#C79A3B] outline-none text-xs py-0.5" />
                  </div>
                )}
              </td>
              <td className="px-2 py-2 align-top">
                <select data-testid={`pricing-unit-${it.key}`} value={it.unit} onChange={(e) => setField(it.key, "unit", e.target.value)} className="border border-[#E5E7EB] bg-white px-1 py-1 text-xs">
                  {["fixed", "each", "sqm", "lm"].map((u) => <option key={u} value={u}>{({ fixed: "Fixed", each: "Each", sqm: "Per m²", lm: "Per lm" })[u]}</option>)}
                </select>
                {isDirty("unit") && <Dot />}
              </td>
              <td className="px-2 py-2 align-top text-right">
                <div className="inline-flex items-center gap-0.5 border-b border-[#12263A]">
                  <span className="text-[#6B7280] text-xs">£</span>
                  <input data-testid={`pricing-price-${it.key}`} type="number" step="0.01" value={it.price ?? 0}
                    onChange={(e) => setField(it.key, "price", e.target.value === "" ? "" : parseFloat(e.target.value))}
                    className="w-20 py-1 font-bold text-right outline-none bg-transparent" />
                </div>
                {isDirty("price") && <Dot />}
                {amber && <div data-testid={`pricing-flag-${it.key}`} title="More than 20% from benchmark" className="text-[9px] font-bold text-[#B45309] bg-[#FEF3C7] px-1 mt-0.5 inline-block">±20%</div>}
              </td>
              <td className="px-2 py-2 align-top text-center">
                <Toggle on={!!it.show_to_client} onClick={() => setField(it.key, "show_to_client", !it.show_to_client)} testid={`pricing-client-toggle-${it.key}`} />
                {isDirty("show_to_client") && <Dot />}
              </td>
              <td className="px-2 py-2 align-top text-center whitespace-nowrap">
                <button data-testid={`pricing-expand-${it.key}`} onClick={() => toggleExpand(it.key)} className="text-[#6B7280] hover:text-[#12263A] inline-flex items-center">
                  {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </button>
                {!it.builtin && (it.archived
                  ? <button data-testid={`pricing-archive-${it.key}`} onClick={() => archive(it)} title="Restore" className="text-[#1F6B3B] ml-1"><ArchiveRestore size={14} /></button>
                  : <button data-testid={`pricing-archive-${it.key}`} onClick={() => archive(it)} title="Archive" className="text-[#9A6B12] ml-1"><Archive size={14} /></button>)}
                {!it.builtin && it.archived && <button data-testid={`pricing-delete-${it.key}`} onClick={() => del(it)} title="Delete" className="text-[#C2401F] ml-1"><Trash2 size={14} /></button>}
              </td>
            </tr>
            {open && (
              <tr data-testid={`pricing-drawer-${it.key}`} className="bg-[#FBFAF8] border-t border-[#E5E7EB]">
                <td colSpan={5} className="px-4 py-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Wickes product URL {isDirty("wickes_url") && <Dot />}</span>
                      <input data-testid={`pricing-wickes-url-${it.key}`} value={it.wickes_url || ""} placeholder="Paste comparable Wickes URL" onChange={(e) => setField(it.key, "wickes_url", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1 outline-none" />
                    </label>
                    <div data-testid={`pricing-wickes-${it.key}`} className="text-[#6B7280]">
                      <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] block">Wickes benchmark (auto)</span>
                      <strong>{gbp(it.wickes_price)}</strong>
                      <span className="block text-[10px] text-[#9CA3AF]">{(it.wickes_status === "stale" || it.wickes_status === "curated") ? `${it.wickes_status} · ` : ""}updated {fmtDate(it.wickes_updated_at)}</span>
                    </div>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Lords price (manual £) {isDirty("lords_price") && <Dot />}</span>
                      <input data-testid={`pricing-lords-${it.key}`} type="number" step="0.01" value={it.lords_price ?? ""} placeholder="—" onChange={(e) => setField(it.key, "lords_price", e.target.value === "" ? "" : parseFloat(e.target.value))} className="w-full border-b border-[#E5E7EB] py-1 outline-none" />
                      <span className="text-[10px] text-[#9CA3AF]">checked {fmtDate(it.lords_checked_at)} · market: {gbp(it.market_benchmark)}</span>
                    </label>
                  </div>
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </>
  );
}

function AddItemForm({ categories, units, onClose, onCreated }) {
  const [f, setF] = useState({ label: "", key: "", category: categories[categories.length - 1] || "Extras", unit: "fixed", price: "", show_to_client: true, wickes_url: "", lords_price: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.label.trim()) { toast.error("Enter a client-facing label"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/pricing/item", { ...f, price: f.price === "" ? 0 : parseFloat(f.price), lords_price: f.lords_price === "" ? null : parseFloat(f.lords_price) });
      toast.success("Item added — available to the quote engine");
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add item"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pricing-add-form" onClick={onClose}>
      <div className="bg-white w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-black mb-1">Add pricing item</h3>
        <p className="text-xs text-[#6B7280] mb-4">New items become optional add-on extras in the configurator (when shown to the client) and feed the instant quote.</p>
        <div className="space-y-3">
          <label className="block text-sm">Client-facing label
            <input data-testid="pricing-new-label" value={f.label} onChange={(e) => upd("label", e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 mt-1" placeholder="e.g. Outdoor garden tap" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">Internal key (optional)
              <input data-testid="pricing-new-key" value={f.key} onChange={(e) => upd("key", e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 mt-1 font-mono text-xs" placeholder="auto from label" /></label>
            <label className="block text-sm">Category
              <select data-testid="pricing-new-category" value={f.category} onChange={(e) => upd("category", e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 mt-1 bg-white">{categories.map((c) => <option key={c}>{c}</option>)}</select></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">Unit
              <select data-testid="pricing-new-unit" value={f.unit} onChange={(e) => upd("unit", e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 mt-1 bg-white">{units.map((u) => <option key={u} value={u}>{({ fixed: "Fixed", each: "Each", sqm: "Per m²", lm: "Per lm" })[u]}</option>)}</select></label>
            <label className="block text-sm">Our price £
              <input data-testid="pricing-new-price" type="number" value={f.price} onChange={(e) => upd("price", e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 mt-1" /></label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input data-testid="pricing-new-visible" type="checkbox" checked={f.show_to_client} onChange={(e) => upd("show_to_client", e.target.checked)} /> Show price in the configurator (offers the item as an optional extra with its price)</label>
          <div className="flex justify-end gap-2 pt-2">
            <button data-testid="pricing-new-cancel" onClick={onClose} className="text-[#6B7280] text-sm px-3">Cancel</button>
            <button data-testid="pricing-new-save" onClick={save} disabled={busy} className="de-btn-primary disabled:opacity-50">{busy ? "Adding…" : "Add item"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
